import * as i0 from '@angular/core';
import { Injector, Injectable, Inject, Component, Optional, ViewChild, Input, HostListener, NgModule } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { PANEL_DATA } from '@tangilla/tg-ng-models/src/lib/view-models';
import { map, shareReplay, take } from 'rxjs/operators';
import { Subject } from 'rxjs';
import * as i1 from '@angular/cdk/layout';
import { Breakpoints } from '@angular/cdk/layout';
import { LayoutServicesModule } from '@tangilla/tg-ng-layout/src/lib/layout-services';
import * as i2 from '@tangilla/tg-ng-layout/src/lib/bottom-sheet-layout';
import * as i3 from '@angular/common';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import * as i3$1 from '@tangilla/tg-ng-layout/src/lib/panel-layout';
import { PanelLayoutModule } from '@tangilla/tg-ng-layout/src/lib/panel-layout';
import * as i4 from '@angular/material/sidenav';
import { MatSidenavModule } from '@angular/material/sidenav';
import * as i5 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';

class ComponentSidePanelService {
    constructor(breakpointObserver, componentBottomSheetService, env) {
        this.breakpointObserver = breakpointObserver;
        this.componentBottomSheetService = componentBottomSheetService;
        this.env = env;
        this.sidePanels = [];
        this.styles = [];
        this.classes = [];
        this.hasBackdrop = [];
        this.panelConfigs = [];
        this.components = [];
        this.injectors = [];
        this.afterClosedSubject = [];
        this.isXSmall$ = this.breakpointObserver.observe(Breakpoints.XSmall)
            .pipe(map(result => result.matches), shareReplay());
        this.isXSmall$.subscribe(data => {
            this.isXSmall = data;
        });
    }
    async openResponsivePanel(component, data, config) {
        if (this.isXSmall) {
            return this.componentBottomSheetService.openComponentBottomSheet(component, data);
        }
        else {
            return await this.openPanel(component, data, config);
        }
    }
    async openPanel(component, data, config) {
        this.afterClosedSubject.push(new Subject());
        const panelLevel = this.components.length;
        const defaultConfig = {
            closeOnBackdropClick: true
        };
        this.applyPanelConfig(panelLevel, {
            ...defaultConfig,
            ...config
        });
        data.panelLevel = panelLevel;
        const hasBackdrop = config?.hasBackdrop || config?.hasBackdrop === undefined ? true : false;
        let pushedBackdrop = false;
        if (hasBackdrop || (!hasBackdrop && this.hasBackdrop.length === 0)) {
            this.hasBackdrop.push(hasBackdrop);
            pushedBackdrop = true;
        }
        const injector = this.createComponentInjector(data);
        this.injectors.push(injector);
        this.components.push(component);
        this.sidePanels[panelLevel]?.closedStart
            .pipe(take(1))
            .subscribe((data) => {
            this.injectors.splice(panelLevel, 1);
            this.components.splice(panelLevel, 1);
            this.styles.splice(panelLevel, 1);
            this.classes.splice(panelLevel, 1);
            this.hasBackdrop.splice(panelLevel, 1);
            this.panelConfigs.splice(panelLevel, 1);
            this.afterClosedSubject[panelLevel].next(this.result);
            this.afterClosedSubject[panelLevel].complete();
            this.afterClosedSubject.splice(panelLevel, 1);
        });
        this.bottomSheetContainer = document.getElementsByClassName('tg-bottom-sheet-container')[0];
        this.bottomSheetBackdrop = document.getElementsByClassName('tg-bottom-sheet-backdrop')[0];
        if (this.bottomSheetContainer) {
            this.bottomSheetContainer.style.display = 'none';
        }
        if (this.bottomSheetBackdrop) {
            this.bottomSheetBackdrop.style.display = 'none';
        }
        const observable = this.afterClosedSubject[panelLevel]?.pipe(take(1));
        await this.sidePanels[panelLevel]?.open();
        if (!hasBackdrop && !pushedBackdrop) {
            this.hasBackdrop.push(hasBackdrop);
        }
        return observable;
    }
    closePanel(result, panelLevel) {
        this.result = result;
        if (this.components.length > 0) {
            if (this.bottomSheetContainer) {
                this.bottomSheetContainer.style.display = 'flex';
            }
            if (this.bottomSheetBackdrop) {
                this.bottomSheetBackdrop.style.display = 'block';
            }
        }
        return this.sidePanels[panelLevel || this.components.length - 1]?.close();
    }
    closeAllPanels(result) {
        this.result = result;
        return this.sidePanels.map(panel => panel.close());
    }
    applyPanelConfig(panelLevel, config) {
        const zIndexStyle = `z-index: ${1001 + panelLevel}; `;
        if (config) {
            config.style = config.style ? `${zIndexStyle}` : zIndexStyle;
        }
        else {
            config = {
                style: zIndexStyle
            };
        }
        this.panelConfigs.push(config ? config : {});
        this.styles.push(config?.style || '');
        this.classes.push(config?.classes || '');
    }
    createComponentInjector(data) {
        data.layoutType = 'side-panel';
        const injector = Injector.create({
            providers: [{ provide: PANEL_DATA, useValue: data }]
        });
        return injector;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComponentSidePanelService, deps: [{ token: i1.BreakpointObserver }, { token: i2.ComponentBottomSheetService }, { token: 'env' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComponentSidePanelService, providedIn: LayoutServicesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComponentSidePanelService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: LayoutServicesModule
                }]
        }], ctorParameters: function () { return [{ type: i1.BreakpointObserver }, { type: i2.ComponentBottomSheetService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['env']
                }] }]; } });

class ConfirmSidePanelComponent {
    constructor(componentSidePanelService, panelProps, bottomSheetProps) {
        this.componentSidePanelService = componentSidePanelService;
        this.panelProps = panelProps;
        this.bottomSheetProps = bottomSheetProps;
        this.props = panelProps || bottomSheetProps;
    }
    closePanel() {
        this.componentSidePanelService.closePanel(false, this.props.panelLevel);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ConfirmSidePanelComponent, deps: [{ token: ComponentSidePanelService }, { token: PANEL_DATA, optional: true }, { token: MAT_BOTTOM_SHEET_DATA, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ConfirmSidePanelComponent, selector: "tg-confirm-side-panel", ngImport: i0, template: "<tg-panel-layout title=\"{{props.title}}\" [mainTitle]=\"false\" [icon]=\"props.icon\"\n                  [hasPrimaryButton]=\"props.showBackButton\"\n                  primaryButtonLabel=\"Back\"\n                  (primaryButtonClick)=\"closePanel()\">\n  <ng-container\n    [ngTemplateOutlet]=\"props.contentTemplate\"\n    [ngTemplateOutletContext]=\"{ $implicit: props.templateItem, item: props.templateItem }\"></ng-container>\n</tg-panel-layout>\n", styles: [".no-result-message-wrapper{margin-top:15px}.no-results-message-icon{margin-bottom:15px;color:#f27447}\n"], dependencies: [{ kind: "directive", type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: i3$1.PanelLayoutComponent, selector: "tg-panel-layout", inputs: ["props", "title", "mainTitle", "subtitle", "icon", "iconClass", "topOfTitleTemplate", "panelContainerClass", "hasPrimaryButton", "primaryButtonLabel", "primaryButtonDisabled", "primaryButtonClass", "hidePrimaryButton", "hasSecondaryButton", "showBottomSheetSecondaryButton", "secondaryButtonLabel", "secondaryButtonDisabled", "secondaryButtonClass", "hideSecondaryButton", "buttonsAlign"], outputs: ["primaryButtonClick", "secondaryButtonClick"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ConfirmSidePanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tg-confirm-side-panel', template: "<tg-panel-layout title=\"{{props.title}}\" [mainTitle]=\"false\" [icon]=\"props.icon\"\n                  [hasPrimaryButton]=\"props.showBackButton\"\n                  primaryButtonLabel=\"Back\"\n                  (primaryButtonClick)=\"closePanel()\">\n  <ng-container\n    [ngTemplateOutlet]=\"props.contentTemplate\"\n    [ngTemplateOutletContext]=\"{ $implicit: props.templateItem, item: props.templateItem }\"></ng-container>\n</tg-panel-layout>\n", styles: [".no-result-message-wrapper{margin-top:15px}.no-results-message-icon{margin-bottom:15px;color:#f27447}\n"] }]
        }], ctorParameters: function () { return [{ type: ComponentSidePanelService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [PANEL_DATA]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [MAT_BOTTOM_SHEET_DATA]
                }] }]; } });

class PagePanelService extends ComponentSidePanelService {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PagePanelService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PagePanelService, providedIn: LayoutServicesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PagePanelService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: LayoutServicesModule
                }]
        }] });

class ComponentSidePanelComponent {
    constructor(pagePanelService, componentSidePanelService) {
        this.pagePanelService = pagePanelService;
        this.componentSidePanelService = componentSidePanelService;
    }
    ngOnInit() {
        this.panelService = this.isPagePanel ? this.pagePanelService : this.componentSidePanelService;
        this.panelService.sidePanels.unshift(this.componentSidePanel);
    }
    async closePanel() {
        return this.panelService.closePanel(null, this.panelLevel);
    }
    getPanelMode() {
        return this.panelService?.panelConfigs[this.panelLevel]?.panelMode || 'over';
    }
    onBackdropClick(event) {
        if (this.panelService?.panelConfigs[this.panelLevel]?.closeOnBackdropClick) {
            this.closeHighestPanel();
        }
    }
    onEscape(event) {
        event.stopImmediatePropagation();
        this.closeHighestPanel();
    }
    closeHighestPanel() {
        const highestPanel = this.getHighestPanelLevel();
        this.panelService.closePanel({ escape: true }, highestPanel);
    }
    getHighestPanelLevel() {
        return this.panelService.sidePanels.filter(panel => panel.opened).length - 1;
    }
    isItPagePanel() {
        return this.isPagePanel || this.panelService.panelConfigs[this.panelLevel]?.isPagePanel;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComponentSidePanelComponent, deps: [{ token: PagePanelService }, { token: ComponentSidePanelService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ComponentSidePanelComponent, selector: "tg-component-side-panel", inputs: { panelLevel: "panelLevel", isPagePanel: "isPagePanel" }, host: { listeners: { "document:keydown.escape": "onEscape($event)" } }, viewQueries: [{ propertyName: "componentSidePanel", first: true, predicate: ["componentSidePanel"], descendants: true, static: true }], ngImport: i0, template: "<mat-sidenav-container\n  class=\"sidenav-container {{ this.isPagePanel ? 'page-panel-sidenav-container' : 'panel-sidenav-container'}}-{{panelLevel}}\"\n  [hasBackdrop]=\"panelLevel === getHighestPanelLevel() && panelService.hasBackdrop[getHighestPanelLevel()]\"\n  (backdropClick)=\"onBackdropClick($event)\">\n  <mat-sidenav\n    class=\"component-sidenav cs-lg {{panelService.classes[panelLevel] || ''}} {{this.panelService.panelConfigs[panelLevel]?.isPagePanel ? 'page-panel-sidenav' : ''}} {{ this.isPagePanel ? 'page-panel-sidenav' : 'panel-sidenav'}}-{{panelLevel}}\"\n    #componentSidePanel\n    opened=\"true\"\n    role=\"dialog\"\n    [mode]=\"getPanelMode()\"\n    position=\"end\"\n    [autoFocus]=\"false\"\n    style=\"{{panelService.styles[panelLevel]}} {{this.isPagePanel ? 'width: 100%' : ''}}\"\n    [opened]=\"false\"\n    [disableClose]=\"true\">\n    <div class=\"component-sidenav-panel-header\"\n         *ngIf=\"!panelService.panelConfigs[panelLevel]?.hideCloseButton && !isPagePanel\">\n      <mat-icon fontSet=\"fa-light\"\n                class=\"close-icon close-icon-{{panelService.panelConfigs[panelLevel]?.closeIconSide ? panelService.panelConfigs[panelLevel]?.closeIconSide : panelLevel === 0 ? 'right' : 'left'}}\"\n                *ngIf=\"!this.componentSidePanelService.hideCloseButton\"\n                fontIcon=\"{{panelService.panelConfigs[panelLevel]?.closeIcon ? panelService.panelConfigs[panelLevel]?.closeIcon : panelLevel === 0 ? 'fa-times': 'fa-chevron-left'}}\"\n                (click)=\"closePanel()\"></mat-icon>\n    </div>\n    <ng-container *ngIf=\"panelService.components[panelLevel]\">\n      <ng-container\n        *ngComponentOutlet=\"panelService.components[panelLevel]; injector: panelService.injectors[panelLevel]\"></ng-container>\n    </ng-container>\n  </mat-sidenav>\n  <mat-sidenav-content\n    class=\"sidenav-content {{panelLevel === 0 && isPagePanel ? 'sidenav-scrollable-content' : ''}}\">\n    <ng-content></ng-content>\n  </mat-sidenav-content>\n</mat-sidenav-container>\n", styles: [".component-sidenav{width:600px}@media (max-width: 600px){.component-sidenav{width:100%}}.component-sidenav.component-sidenav-md{width:800px}@media (max-width: 800px){.component-sidenav.component-sidenav-md{width:100%}}.component-sidenav.component-sidenav-lg{width:950px}@media (max-width: 950px){.component-sidenav.component-sidenav-lg{width:100%}}.component-sidenav.component-sidenav-xl{width:1200px}@media (max-width: 1200px){.component-sidenav.component-sidenav-xl{width:100%}}.close-icon,.close-icon-right,.close-icon-left{font-size:20px;position:absolute;top:25.25px;text-align:center;cursor:pointer}.close-icon-left{left:25.25px}.close-icon-right{right:25.25px}\n"], dependencies: [{ kind: "directive", type: i3.NgComponentOutlet, selector: "[ngComponentOutlet]", inputs: ["ngComponentOutlet", "ngComponentOutletInputs", "ngComponentOutletInjector", "ngComponentOutletContent", "ngComponentOutletNgModule", "ngComponentOutletNgModuleFactory"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.MatSidenav, selector: "mat-sidenav", inputs: ["fixedInViewport", "fixedTopGap", "fixedBottomGap"], exportAs: ["matSidenav"] }, { kind: "component", type: i4.MatSidenavContainer, selector: "mat-sidenav-container", exportAs: ["matSidenavContainer"] }, { kind: "component", type: i4.MatSidenavContent, selector: "mat-sidenav-content" }, { kind: "component", type: i5.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComponentSidePanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tg-component-side-panel', template: "<mat-sidenav-container\n  class=\"sidenav-container {{ this.isPagePanel ? 'page-panel-sidenav-container' : 'panel-sidenav-container'}}-{{panelLevel}}\"\n  [hasBackdrop]=\"panelLevel === getHighestPanelLevel() && panelService.hasBackdrop[getHighestPanelLevel()]\"\n  (backdropClick)=\"onBackdropClick($event)\">\n  <mat-sidenav\n    class=\"component-sidenav cs-lg {{panelService.classes[panelLevel] || ''}} {{this.panelService.panelConfigs[panelLevel]?.isPagePanel ? 'page-panel-sidenav' : ''}} {{ this.isPagePanel ? 'page-panel-sidenav' : 'panel-sidenav'}}-{{panelLevel}}\"\n    #componentSidePanel\n    opened=\"true\"\n    role=\"dialog\"\n    [mode]=\"getPanelMode()\"\n    position=\"end\"\n    [autoFocus]=\"false\"\n    style=\"{{panelService.styles[panelLevel]}} {{this.isPagePanel ? 'width: 100%' : ''}}\"\n    [opened]=\"false\"\n    [disableClose]=\"true\">\n    <div class=\"component-sidenav-panel-header\"\n         *ngIf=\"!panelService.panelConfigs[panelLevel]?.hideCloseButton && !isPagePanel\">\n      <mat-icon fontSet=\"fa-light\"\n                class=\"close-icon close-icon-{{panelService.panelConfigs[panelLevel]?.closeIconSide ? panelService.panelConfigs[panelLevel]?.closeIconSide : panelLevel === 0 ? 'right' : 'left'}}\"\n                *ngIf=\"!this.componentSidePanelService.hideCloseButton\"\n                fontIcon=\"{{panelService.panelConfigs[panelLevel]?.closeIcon ? panelService.panelConfigs[panelLevel]?.closeIcon : panelLevel === 0 ? 'fa-times': 'fa-chevron-left'}}\"\n                (click)=\"closePanel()\"></mat-icon>\n    </div>\n    <ng-container *ngIf=\"panelService.components[panelLevel]\">\n      <ng-container\n        *ngComponentOutlet=\"panelService.components[panelLevel]; injector: panelService.injectors[panelLevel]\"></ng-container>\n    </ng-container>\n  </mat-sidenav>\n  <mat-sidenav-content\n    class=\"sidenav-content {{panelLevel === 0 && isPagePanel ? 'sidenav-scrollable-content' : ''}}\">\n    <ng-content></ng-content>\n  </mat-sidenav-content>\n</mat-sidenav-container>\n", styles: [".component-sidenav{width:600px}@media (max-width: 600px){.component-sidenav{width:100%}}.component-sidenav.component-sidenav-md{width:800px}@media (max-width: 800px){.component-sidenav.component-sidenav-md{width:100%}}.component-sidenav.component-sidenav-lg{width:950px}@media (max-width: 950px){.component-sidenav.component-sidenav-lg{width:100%}}.component-sidenav.component-sidenav-xl{width:1200px}@media (max-width: 1200px){.component-sidenav.component-sidenav-xl{width:100%}}.close-icon,.close-icon-right,.close-icon-left{font-size:20px;position:absolute;top:25.25px;text-align:center;cursor:pointer}.close-icon-left{left:25.25px}.close-icon-right{right:25.25px}\n"] }]
        }], ctorParameters: function () { return [{ type: PagePanelService }, { type: ComponentSidePanelService }]; }, propDecorators: { componentSidePanel: [{
                type: ViewChild,
                args: ['componentSidePanel', { static: true }]
            }], panelLevel: [{
                type: Input
            }], isPagePanel: [{
                type: Input
            }], onEscape: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event']]
            }] } });

class ComponentSidePanelModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComponentSidePanelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: ComponentSidePanelModule, declarations: [ComponentSidePanelComponent,
            ConfirmSidePanelComponent], imports: [CommonModule,
            MatSidenavModule,
            MatIconModule,
            PanelLayoutModule,
            NgTemplateOutlet], exports: [ConfirmSidePanelComponent,
            ComponentSidePanelComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComponentSidePanelModule, imports: [CommonModule,
            MatSidenavModule,
            MatIconModule,
            PanelLayoutModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComponentSidePanelModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ComponentSidePanelComponent,
                        ConfirmSidePanelComponent
                    ],
                    imports: [
                        CommonModule,
                        MatSidenavModule,
                        MatIconModule,
                        PanelLayoutModule,
                        NgTemplateOutlet
                    ],
                    exports: [
                        ConfirmSidePanelComponent,
                        ComponentSidePanelComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ComponentSidePanelComponent, ComponentSidePanelModule, ComponentSidePanelService, ConfirmSidePanelComponent, PagePanelService };
